import { jsPDF } from 'jspdf';
import { SELLER, CUSTOMERS, PRODUCTS, parseCommand, computeQuote } from './data.js';
import { buildQuotePdf } from './pdf.js';
import { FONTS } from './fonts.js';
import { wait, rand, tl, pad, now, today, hex } from './helpers.js';
import { getState, setState, audit, show, toBottom, setSys, setCmd, focusCmd, updateAgent } from './store.js';

let uid = 0;
const nextId = () => ++uid;
const timers = {};

// ---------- agent card ----------
function createAgent() {
  const id = nextId();
  setState(s => ({
    chat: [...s.chat, { kind: 'agent', id, state: 'çalışıyor', timer: '0,0 sn', steps: [], steps2: null, ask: null, plan: null, planDisabled: false, guard: null, result: null, mail: null }]
  }));
  const t0 = performance.now();
  timers[id] = { iv: setInterval(() => {
    updateAgent(id, a => ({ ...a, timer: ((performance.now() - t0) / 1000).toFixed(1).replace('.', ',') + ' sn' }));
  }, 100) };
  return id;
}
const finish = (id, label) => { clearInterval(timers[id].iv); updateAgent(id, a => ({ ...a, state: label })); };
function resume(id, label) {
  const a = getState().chat.find(c => c.id === id);
  const base = parseFloat(a.timer.replace(',', '.')) || 0;
  const t1 = performance.now();
  updateAgent(id, x => ({ ...x, state: label }));
  timers[id].iv2 = setInterval(() => {
    updateAgent(id, x => ({ ...x, timer: (base + (performance.now() - t1) / 1000).toFixed(1).replace('.', ',') + ' sn' }));
  }, 100);
}
const stop = (id, label) => { clearInterval(timers[id].iv2); updateAgent(id, a => ({ ...a, state: label })); };

async function step(agId, key, title, srcCls, srcLabel, subs, range, opts) {
  opts = opts || {};
  const sid = nextId();
  const upd = fn => updateAgent(agId, a => ({ ...a, [key]: a[key].map(s => (s.id === sid ? fn(s) : s)) }));
  updateAgent(agId, a => ({ ...a, [key]: [...a[key], { id: sid, title, srcCls, srcLabel, status: 'run', subs: [], dur: '' }] }));
  toBottom();
  if (opts.sys) setSys(opts.sys, true);
  const total = rand(range[0], range[1]);
  const start = performance.now();
  const list = subs || [];
  // sub-lines arrive at irregular moments inside the step
  const marks = list.map(() => Math.random()).sort().map(x => x * total * 0.9);
  for (let i = 0; i < list.length; i++) {
    const elapsed = performance.now() - start;
    await wait(Math.max(0, marks[i] - elapsed));
    const item = list[i];
    upd(s => ({ ...s, subs: [...s.subs, { w: !!item.w, t: item.t || item }] }));
    toBottom();
  }
  await wait(Math.max(0, total - (performance.now() - start)));
  if (opts.sys) setSys(opts.sys, false);
  upd(s => ({ ...s, status: opts.warn ? 'warn' : 'done', dur: (total / 1000).toFixed(1).replace('.', ',') + ' sn' }));
  audit(title + (srcLabel ? ' [' + srcLabel + ']' : '') + ' — ' + (total / 1000).toFixed(1) + ' sn');
}

// ---------- submit ----------
export async function submit() {
  const s = getState();
  const text = s.cmd.trim();
  if (!text || s.busy) return;
  const d = new Date();
  setState(st => ({
    cmd: '', busy: true,
    chat: [...st.chat, { kind: 'user', id: nextId(), text, meta: 'Said Berk · ' + pad(d.getHours()) + ':' + pad(d.getMinutes()) }]
  }));
  audit('Komut alındı (Web): "' + text + '"');
  toBottom();
  try { await runAgent(text); }
  catch (e) { console.error(e); }
  setState({ busy: false });
  focusCmd();
}

async function runAgent(text) {
  const agId = createAgent();
  const parsed = parseCommand(text);
  const c = parsed.customer;
  const missingQty = parsed.lines.filter(l => !l.qty);

  await step(agId, 'steps', 'Talep çözümleniyor', 'ai', 'LLM', [
    'Niyet: fiyat teklifi oluştur',
    c ? 'Müşteri: ' + c.name : { t: 'Müşteri adı tespit edilemedi', w: 1 },
    parsed.lines.length ? parsed.lines.length + ' kalem tespit edildi: ' + parsed.lines.map(l => (l.qty ? l.qty + ' × ' : '') + l.product.name).join(', ') : { t: 'Ürün tespit edilemedi', w: 1 }
  ], [2600, 4200]);

  if (!c || !parsed.lines.length || missingQty.length) {
    finish(agId, 'bilgi gerekiyor');
    const miss = [];
    if (!c) miss.push('müşteri');
    if (!parsed.lines.length) miss.push('ürün');
    if (missingQty.length) miss.push(missingQty.map(l => l.product.name).join(', ') + ' için miktar');
    let options = [];
    if (!c) options = CUSTOMERS.map(cu => ({ label: cu.name, value: cu.name.split(' ').slice(0, 2).join(' ') + ' için ' + text }));
    else if (!parsed.lines.length) options = PRODUCTS.slice(0, 6).map(p => ({ label: p.name, value: text + ' — 50 ' + p.unit + ' ' + p.alias[0] }));
    updateAgent(agId, a => ({ ...a, ask: { message: 'Teklifi hazırlamak için şu bilgi eksik: ' + miss.join(', ') + '. Aşağıdakilerden birini seçip komutu tamamlayabilirsiniz.', options } }));
    toBottom();
    audit('Eksik bilgi nedeniyle kullanıcıya soru soruldu');
    return;
  }

  const q = computeQuote(parsed);
  const seq = getState().seq + 1;
  setState({ seq });
  const quoteNo = 'TKL-' + new Date().getFullYear() + '-' + String(seq).padStart(5, '0');
  const rows = rand(1180, 1320);
  const short = c.name.split(' ')[0];

  await step(agId, 'steps', 'Ofis bilgisayarları taranıyor', 'xl', 'Dosya sistemi', [
    'Ofis Bilgisayarı 1 (SATIS-PC01): \\Belgeler\\Fiyat Listeleri taranıyor',
    'Ofis Bilgisayarı 2 (MUHASEBE-PC02): \\Muhasebe\\Cari klasöründe 2 Excel dosyası bulundu',
    'Ofis Bilgisayarı 3 (DEPO-PC03): stok_sayim_eylul_2026.xlsx bulundu',
    'Ofis Bilgisayarı 4 (URETIM-PC04): uretim_plani_ekim.xlsx bulundu',
    'İlgili 5 dosya belirlendi, en güncel sürümler seçildi'
  ], [5200, 8000], { sys: ['pc1', 'pc2', 'pc3', 'pc4'] });

  const xlSubs = [
    'fiyat_listesi_2026_Q3.xlsx açıldı (PC1), ' + rows.toLocaleString('tr-TR') + ' satır okundu',
    q.items.length + ' kalem fiyat listesiyle eşleşti: ' + q.items.map(i => i.code).join(', '),
    'iskonto_matrisi.xlsx (PC2): ' + short + ' için müşteri iskontosu %' + c.discount + ', vade ' + c.payTerm + ' gün',
    'stok_sayim_eylul_2026.xlsx (PC3): sayım ile SAP stoğu karşılaştırıldı, fark yok'
  ];
  if (q.items.some(i => !i.stockOk)) xlSubs.push('uretim_plani_ekim.xlsx (PC4): eksik kalemler için 41. hafta CNC kapasitesi uygun');
  await step(agId, 'steps', 'Excel dosyaları inceleniyor', 'xl', 'Excel', xlSubs, [5200, 8400], { sys: ['pc1', 'pc2', 'pc3', 'pc4'] });

  const sapSubs = [
    'RFC bağlantısı kuruldu (S4H-PRD, istemci 100)',
    'Müşteri ana verisi okundu: KNA1 · ' + c.id,
    'Malzeme kayıtları okundu: MARA / MARD'
  ];
  q.items.forEach(i => sapSubs.push(i.stockOk
    ? { t: i.code + ' stok: ' + i.stock + ' ' + i.unit + ' (' + i.depot + '), yeterli' }
    : { t: i.code + ' stok: ' + i.stock + ' ' + i.unit + ', talep ' + i.qty + ', eksik ' + (i.qty - i.stock), w: 1 }));
  sapSubs.push('Açık siparişler kontrol edildi, rezerve stok düşüldü');
  await step(agId, 'steps', 'SAP sistemi sorgulanıyor', 'sap', 'SAP ERP', sapSubs, [7200, 10400], { sys: ['sap'], warn: q.items.some(i => !i.stockOk) });

  await step(agId, 'steps', 'Logo ve teklif geçmişi inceleniyor', 'db', 'Logo ERP', [
    'Logo cari kartı eşleşti: 120.01.' + c.id.slice(-3),
    'Açık bakiye: ' + tl(c.openBalance) + ' / kredi limiti ' + tl(c.creditLimit),
    'teklif_takip_2026.xlsx (PC1): ' + short + ' için ' + c.pastQuotes + ' geçmiş teklif, son kazanılan ' + c.lastWin,
    'Fiyat sapması kontrolü: son teklife göre %' + (rand(0, 30) / 10).toFixed(1).replace('.', ',') + ', eşik altında'
  ], [4200, 7000], { sys: ['logo', 'pc1'] });

  const ruleSubs = ['Müşteri iskontosu uygulandı: %' + c.discount];
  if (q.items.some(i => i.qty >= 100)) ruleSubs.push('Hacim iskontosu (100+ adet): +%3');
  ruleSubs.push('KDV %20 hesaplandı: ' + tl(q.vat));
  ruleSubs.push(q.creditOk ? 'Kredi limiti kontrolü: uygun' : { t: 'Kredi limiti aşılıyor: %30 ön ödeme şartı eklendi', w: 1 });
  ruleSubs.push('Teslim süresi hesaplandı: ' + q.maxLead + ' iş günü');
  await step(agId, 'steps', 'İş kuralları uygulanıyor', 'rule', 'Kurallar', ruleSubs, [3000, 5400], { warn: !q.creditOk });

  await step(agId, 'steps', 'Teklif taslağı hazırlanıyor', 'ai', 'LLM', [
    'Taslak numarası atandı: ' + quoteNo,
    'Kalemler, toplamlar ve ticari şartlar yazıldı',
    q.notes.length ? q.notes.length + ' uyarı notu eklendi' : 'Uyarı gerektiren durum yok',
    'Onaya sunuluyor'
  ], [3000, 5000]);

  finish(agId, 'onay bekliyor');

  const draft = Object.assign({}, q, { no: quoteNo, customer: c, seller: SELLER, command: text, date: today(0), validUntil: today(15), agId });
  setState(s => ({ pending: [...s.pending, draft] }));
  updateAgent(agId, a => ({ ...a, plan: draft, guard: "Onayınız olmadan SAP'ye yazılmaz ve müşteriye gönderilmez." }));
  audit('Taslak ' + quoteNo + ' onaya sunuldu (' + tl(q.total) + ')');
  toBottom();
}

// ---------- plan actions ----------
export function editDraft(q) { setCmd(q.command); focusCmd(); }

export function cancelDraft(q) {
  updateAgent(q.agId, a => ({ ...a, planDisabled: true, guard: 'Taslak iptal edildi. Hiçbir kayıt yazılmadı.' }));
  setState(s => ({ pending: s.pending.filter(p => p !== q) }));
  finish(q.agId, 'iptal edildi');
  audit('Taslak ' + q.no + ' kullanıcı tarafından iptal edildi');
}

export async function approve(q) {
  if (q._approved) return;
  q._approved = true;
  const agId = q.agId;
  updateAgent(agId, a => ({ ...a, planDisabled: true, steps2: [] }));
  setState(s => ({ pending: s.pending.filter(p => p !== q) }));
  show('chat');
  resume(agId, 'onaylandı, işleniyor');
  const approvedAt = now();
  audit('Taslak ' + q.no + ' Said Berk tarafından onaylandı');

  await step(agId, 'steps2', 'Onay kaydedildi', 'rule', 'Yetki', ['Kullanıcı: Said Berk (satış operasyon, teklif onay yetkisi var)', 'Zaman damgası: ' + approvedAt], [1400, 2600]);

  const txId = 'VZ-' + hex(4) + '-' + hex(4);
  const createdAt = approvedAt.slice(0, 16);
  const full = Object.assign({}, q, {
    txId, createdAt, approvedAt: createdAt, approvedAtShort: createdAt,
    preparedBy: 'VİZOVA Asistanı', preparedRole: 'Said Berk adına, otomatik taslak',
    approvedBy: 'Said Berk', approvedRole: 'Satış Operasyon Uzmanı'
  });

  let blob = null, err = null;
  const pdfStep = step(agId, 'steps2', "Teklif PDF'i oluşturuluyor", 'ai', 'Belge', ['Şablon: Anadolu Makina kurumsal teklif', 'Kalemler, toplamlar ve ticari şartlar yerleştirildi', 'Tutar yazıya çevrildi'], [2600, 4400]);
  try {
    const doc = buildQuotePdf(jsPDF, full, FONTS);
    blob = doc.output('blob');
  } catch (e) { err = e; console.error(e); }
  await pdfStep;

  full.sapDoc = '20000' + rand(41900, 41999);
  await step(agId, 'steps2', "SAP'ye teklif kaydı yazılıyor", 'sap', 'SAP ERP', ['İşlem: VA21 (satış teklifi oluştur)', 'Belge no: ' + full.sapDoc + ' oluşturuldu', 'Yazma doğrulandı, 0 hata'], [2800, 4800], { sys: ['sap'] });
  await step(agId, 'steps2', 'Teklif takip dosyası güncelleniyor', 'xl', 'Excel', ['Ofis Bilgisayarı 1: \\Masaüstü\\teklif_takip_2026.xlsx', 'Yeni satır eklendi: ' + q.no + ' · ' + tl(q.total)], [1600, 2800], { sys: ['pc1'] });
  await step(agId, 'steps2', 'Denetim kaydı oluşturuldu', 'db', 'PostgreSQL', ['İşlem no: ' + txId], [900, 1600], { sys: ['db'] });
  stop(agId, 'tamamlandı');

  const filename = 'VIZOVA_Teklif_' + q.no + '.pdf';
  full.filename = filename; full.blob = blob;
  setState(s => ({ quotes: [full, ...s.quotes] }));

  const meta = blob ? (blob.size / 1024).toFixed(0) + ' KB · 1 sayfa · ' + tl(q.total) : 'PDF oluşturulamadı: ' + (err && err.message);
  const mailBody =
    'Konu: ' + q.no + ' numaralı fiyat teklifimiz\n\n' +
    'Sayın ' + q.customer.contact + ',\n\n' +
    'Talebiniz doğrultusunda hazırladığımız ' + q.no + ' numaralı fiyat teklifimizi ekte bilgilerinize sunarız. ' +
    'Teklifimiz ' + q.items.length + ' kalemden oluşmakta olup KDV dahil toplam tutarı ' + tl(q.total) + '’dir. ' +
    'Teslim süresi sipariş onayından itibaren ' + q.maxLead + ' iş günüdür; teklifimiz ' + q.validUntil + ' tarihine kadar geçerlidir.\n\n' +
    'Sorularınız için bize ulaşabilirsiniz.\n\nİyi çalışmalar,\nSaid Berk\nAnadolu Makina A.Ş.\n\nEk: ' + filename;
  updateAgent(agId, a => ({ ...a, result: { quote: full, filename, meta }, mail: { summary: 'E-posta taslağı (' + q.customer.email + ')', body: mailBody } }));
  toBottom();
  audit('Teklif ' + q.no + ' tamamlandı, PDF hazır (' + txId + ')');
}

// ---------- download ----------
// ui: { setDisabled(bool), setStatus({ cls, text }) }
export function downloadPdf(q, ui) {
  if (!q.blob) return;
  ui.setDisabled(true);
  ui.setStatus({ cls: '', text: 'İndirme hazırlanıyor… Açılan pencerede kaydetmeyi onaylayın.' });
  try {
    const url = URL.createObjectURL(q.blob);
    const a = document.createElement('a');
    a.href = url; a.download = q.filename; a.style.display = 'none';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    ui.setStatus({ cls: 'ok', text: q.filename + ' indirildi.' });
  } catch (e) {
    ui.setStatus({ cls: 'err', text: 'Bu görünümde indirme desteklenmiyor. Sayfayı yayınlanan bağlantıdan açıp tekrar deneyin.' });
  }
  ui.setDisabled(false);
}
